import type { TUserSocialGraphStatusResult } from '@/application/user/user.types';
import type { TReadProfileParams } from '@/controllers/profile/profile.types';
import type { TPubkyListParams } from '@/controllers/user/user.type';
import { db } from '@/database/franky/franky';
import { getTtlUserMs } from '@/libs/runtime-config/runtime-config';
import type { Pubky } from '@/models/models.types';
import { UserCountsModel } from '@/models/user/counts/userCounts';
import type { TUserCountsParams } from '@/models/user/counts/userCounts.types';
import { UserDetailsModel } from '@/models/user/details/userDetails';
import { UserRelationshipsModel } from '@/models/user/relationships/userRelationships';
import type { UserRelationshipsModelSchema } from '@/models/user/relationships/userRelationships.schema';
import { UserTagsModel } from '@/models/user/tags/userTags';
import { UserTtlModel } from '@/models/user/ttl/userTtl';
import { LocalTagCacheService, type TagPreviewGuard } from '@/services/local/tag/tag-cache';
import type { NexusTag, NexusUserCounts, NexusUserDetails, NexusUserRelationship } from '@/services/nexus/nexus.types';

export class LocalUserService {
  private constructor() {} // Prevent instantiation

  /**
   * Reads user details from local database.
   * @param userId - User ID to read details for
   * @returns Promise resolving to user details or null if not found
   */
  static async readDetails({ userId }: TReadProfileParams): Promise<NexusUserDetails | null> {
    return await UserDetailsModel.findById(userId);
  }

  /**
   * Bulk reads multiple user details from local database.
   * @param userIds - Array of user IDs to read details for
   * @returns Promise resolving to Map of user ID to user details
   */
  static async readBulkDetails({ userIds }: TPubkyListParams): Promise<Map<Pubky, NexusUserDetails>> {
    if (userIds.length === 0) return new Map();

    const results = await UserDetailsModel.findByIdsPreserveOrder(userIds);
    const map = new Map<Pubky, NexusUserDetails>();

    for (const details of results) {
      if (details) {
        map.set(details.id, details);
      }
    }

    return map;
  }

  /**
   * Reads a user's social graph badge tier from local database.
   *
   * The tier is only known once a full Nexus user view has been persisted; a details
   * row written from a details-only fetch has no `social_graph_status` yet.
   *
   * @param userId - User ID to read the tier for
   * @returns `{ status }` once known (`status: null` when Nexus has no ranking or the TTL
   *   coordinator will supply it), or `null` when the user is missing locally or was only
   *   ever cached from a details-only fetch (a full fetch is needed)
   */
  static async readSocialGraphStatus({ userId }: TReadProfileParams): Promise<TUserSocialGraphStatusResult | null> {
    const details = await UserDetailsModel.findById(userId);
    if (!details) return null;
    if (details.social_graph_status !== undefined) return { status: details.social_graph_status };

    // A row without the tier came from a details-only write. When a TTL record exists the
    // user is already on the refresh path (a full view persisted before this field existed,
    // or a not-yet-indexed user scheduled for retry), so report "no ranking" and let the TTL
    // coordinator fill the tier in rather than forcing a full fetch that would overwrite
    // fresher local relationship and count rows.
    const ttl = await UserTtlModel.findById(userId);
    return ttl ? { status: null } : null;
  }

  /**
   * Reads user counts from local database.
   * @param userId - The user ID to read counts for
   * @returns Promise resolving to the stored row (including `id`) or null if not found
   */
  static async readCounts({ userId }: TReadProfileParams): Promise<UserCountsModel | null> {
    return await UserCountsModel.findById(userId);
  }

  /**
   * Upserts user counts into local database.
   * Creates a new record if it doesn't exist, or replaces it if it does.
   * @param params - Parameters containing user ID
   * @param userCounts - The user counts to upsert
   * @returns Promise resolving to void
   */
  static async upsertCounts(params: TReadProfileParams, userCounts: NexusUserCounts): Promise<void> {
    // Use proper upsert (put) to create the record if it doesn't exist
    await UserCountsModel.upsert({
      id: params.userId,
      ...userCounts,
    });
  }

  /**
   * Updates user counts in local database by applying count changes.
   * @param params - Parameters containing user ID and count changes
   * @returns Promise resolving to void
   */
  static async updateCounts(params: TUserCountsParams): Promise<void> {
    await UserCountsModel.updateCounts(params);
  }

  /**
   * Bulk reads multiple user counts from local database.
   * @param userIds - Array of user IDs to read counts for
   * @returns Promise resolving to Map of user ID to user counts
   */
  static async readBulkCounts({ userIds }: TPubkyListParams): Promise<Map<Pubky, NexusUserCounts>> {
    if (userIds.length === 0) return new Map();

    const results = await UserCountsModel.findByIdsPreserveOrder(userIds);
    const map = new Map<Pubky, NexusUserCounts>();

    for (const counts of results) {
      if (counts) {
        map.set(counts.id, counts);
      }
    }

    return map;
  }

  /**
   * Reads user relationships from local database.
   * @param userId - User ID to read relationships for
   * @returns Promise resolving to user relationships or null if not found
   */
  static async readRelationships({ userId }: TReadProfileParams): Promise<NexusUserRelationship | null> {
    return await UserRelationshipsModel.findById(userId);
  }

  /**
   * Bulk reads multiple user relationships from local database.
   * @param userIds - Array of user IDs to read relationships for
   * @returns Promise resolving to Map of user ID to user relationships
   */
  static async readBulkRelationships({ userIds }: TPubkyListParams): Promise<Map<Pubky, UserRelationshipsModelSchema>> {
    if (userIds.length === 0) return new Map();

    const results = await UserRelationshipsModel.findByIds(userIds);
    const map = new Map<Pubky, UserRelationshipsModelSchema>();

    for (const relationship of results) {
      if (relationship) {
        map.set(relationship.id, relationship);
      }
    }

    return map;
  }

  /**
   * Bulk reads multiple user tags from local database.
   * @param userIds - Array of user IDs to read tags for
   * @returns Promise resolving to Map of user ID to user tags
   */
  static async readBulkTags({ userIds }: TPubkyListParams): Promise<Map<Pubky, NexusTag[]>> {
    if (userIds.length === 0) return new Map();

    const results = await UserTagsModel.findByIdsPreserveOrder(userIds);
    const map = new Map<Pubky, NexusTag[]>();

    for (const tagsData of results) {
      if (tagsData) {
        map.set(tagsData.id, tagsData.tags);
      }
    }

    return map;
  }

  /**
   * Upserts user tags into local database.
   * Creates a new record if it doesn't exist, or replaces it if it does.
   * @param userId - The user ID
   * @param tags - The user tags to upsert
   * @returns Promise resolving to void
   */
  static async upsertTags(userId: Pubky, tags: NexusTag[], tagGuard?: TagPreviewGuard): Promise<void> {
    await LocalTagCacheService.savePreviews('user', [[userId, tags]], tagGuard);
  }

  /**
   * Upserts user TTL record with a calculated timestamp for delayed refresh.
   * Used to schedule refresh for users not yet indexed in Nexus.
   *
   * The timestamp is calculated as: now - (userTtlMs - retryDelayMs)
   * This ensures the entity becomes stale after the specified delay.
   *
   * @param userId - The user ID
   * @param retryDelayMs - Delay in milliseconds before the entity should become stale.
   *   If retryDelayMs >= the configured user TTL, the entity becomes immediately stale
   *   (triggers immediate refresh on next TTL coordinator tick). This is intentional
   *   and can be useful for forcing immediate refresh.
   * @param options.unlessWrittenSince - Keep a row written at or after this time (a
   *   local write or another successful refresh landed while a batch was in flight),
   *   so a cooldown never shortens real freshness. The check and the write share one
   *   transaction.
   * @returns Promise resolving to void
   */
  static async upsertTtlWithDelay(
    userId: Pubky,
    retryDelayMs: number,
    options: { unlessWrittenSince?: number } = {},
  ): Promise<void> {
    const lastUpdatedAt = Date.now() - (getTtlUserMs() - retryDelayMs);
    await db.transaction('rw', UserTtlModel.table, async () => {
      if (options.unlessWrittenSince !== undefined) {
        const existing = await UserTtlModel.findById(userId);
        if (existing && existing.lastUpdatedAt >= options.unlessWrittenSince) return;
      }
      await UserTtlModel.upsert({ id: userId, lastUpdatedAt });
    });
  }
}
